
import { useEffect, useRef, useState } from 'react';
import NeuralNetworkBg from '../../../components/feature/NeuralNetworkBg';

const steps = [
  {
    number: '01',
    title: 'Identify your state',
    description:
      'Real-time nervous system assessment to understand where you are right now.',
    icon: 'ri-pulse-line',
  },
  {
    number: '02',
    title: 'Select a protocol',
    description:
      'Targeted reconditioning sequences matched to your current neural state.',
    icon: 'ri-list-check-3',
  },
  {
    number: '03',
    title: 'Run guided sequences',
    description:
      'Brain-body integration exercises that rewire your response patterns.',
    icon: 'ri-play-circle-line',
  },
  {
    number: '04',
    title: 'Install a new baseline',
    description:
      'Lasting neurological change that compounds over time.',
    icon: 'ri-download-2-line',
  },
];

export default function System() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="system"
      ref={sectionRef}
      className="relative py-28 lg:py-36 px-6 lg:px-12 overflow-hidden noise-texture"
    >
      {/* Softer dark gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#141118] via-[#1a1520] to-[#141118]"></div>

      {/* Neural network — primary */}
      <NeuralNetworkBg
        color="77,184,199"
        nodeCount={45}
        opacity={0.4}
        speed={0.6}
        variant="dark"
      />

      {/* Neural network — secondary */}
      <NeuralNetworkBg
        color="154,123,212"
        nodeCount={25}
        opacity={0.2}
        speed={0.4}
        variant="dark"
      />

      {/* Soft animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute w-[600px] h-[600px] rounded-full animate-float-slow opacity-12"
          style={{
            background:
              'radial-gradient(circle, rgba(154,123,212,0.35) 0%, transparent 70%)',
            top: '-10%',
            right: '-5%',
          }}
        ></div>
        <div
          className="absolute w-[500px] h-[500px] rounded-full animate-float opacity-10"
          style={{
            background:
              'radial-gradient(circle, rgba(77,184,199,0.3) 0%, transparent 70%)',
            bottom: '-10%',
            left: '-5%',
            animationDelay: '3s',
          }}
        ></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto">
        <div
          className={`text-center mb-20 transition-all duration-1000 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/8 mb-8">
            <div className="w-2 h-2 rounded-full bg-brand-teal animate-pulse"></div>
            <span className="text-xs font-semibold text-brand-teal uppercase tracking-widest">
              The System
            </span>
          </div>

          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 tracking-tight leading-tight">
            A new{' '}
            <span className="gradient-text-animated">operating system</span>
            <br />
            for your brain.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`group relative p-8 lg:p-10 rounded-2xl glass-card hover:bg-white/10 transition-all duration-500 hover:shadow-2xl hover:shadow-brand-violet/8 cursor-pointer animate-border-glow ${
                visible ? 'animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-orange/15 via-brand-pink/15 to-brand-violet/15 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                    <i className={`${step.icon} text-2xl text-white/80`}></i>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-bold text-brand-pink/50 uppercase tracking-widest">
                      {step.number}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 tracking-tight">
                    {step.title}
                  </h3>
                  <p className="text-sm text-white/40 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>

              {index < steps.length - 1 && (
                <div className="hidden md:block absolute -bottom-3 left-1/2 w-px h-6 bg-gradient-to-b from-white/8 to-transparent"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
